import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import YCodeThumbnail from '../components/YCodeThumbnail';
import { API_URL } from '../config/api';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    students: 0,
    courses: 0,
    questionPackages: 0,
    quality: 0
  });

  const [activeFeature, setActiveFeature] = useState(0);
  const [activeAIFeature, setActiveAIFeature] = useState(0);
  const [codeExample, setCodeExample] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [modalContent, setModalContent] = useState({
    icon: '',
    title: '',
    message: '',
    buttons: []
  });

  // Handler for AI feature navigation
  const handleAIFeatureClick = (feature) => {
    const token = localStorage.getItem('token');
    
    // Check if it's RPG Skill Tree
    if (feature.title === 'RPG Skill Tree') {
      if (!token) {
        setModalContent({
          icon: '🔒',
          title: 'Login Required',
          message: 'The RPG Skill Tree is available to all registered users. Sign up or login to unlock your learning journey and start leveling up your coding skills!',
          buttons: [
            {
              text: 'Login / Sign Up',
              action: () => {
                setShowModal(false);
                navigate('/login');
              },
              primary: true
            },
            {
              text: 'Maybe Later',
              action: () => setShowModal(false),
              primary: false
            }
          ]
        });
        setShowModal(true);
        return;
      }
      navigate('/dashboard');
      return;
    }
    
    // Check if it's Ghost Coding or Advanced IDE
    if (feature.title === 'Ghost Coding' || feature.title === 'Advanced IDE') {
      const content = feature.title === 'Ghost Coding' 
        ? {
            icon: '👻',
            title: 'Ghost Coding',
            message: 'Watch keystroke-by-keystroke replays of expert solutions. Learn coding patterns and problem-solving approaches from the best programmers. Available in our Question Packages!'
          }
        : {
            icon: '💻',
            title: 'Advanced IDE',
            message: 'Access our professional-grade IDE with syntax highlighting, autocomplete, real-time error detection, and debugging tools. Available in our Question Packages!'
          };
      
      setModalContent({
        ...content,
        buttons: [
          {
            text: 'Explore Question Packages',
            action: () => {
              setShowModal(false);
              navigate('/question-packages');
            },
            primary: true
          },
          {
            text: 'Close',
            action: () => setShowModal(false),
            primary: false
          }
        ]
      });
      setShowModal(true);
      return;
    }
    
    // For other features, navigate normally
    navigate(feature.link);
  };

  // Animate statistics on load
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = duration / steps;

    const targets = {
      students: 100,
      courses: 6,
      questionPackages: 6,
      quality: 100
    };

    let current = {
      students: 0,
      courses: 0,
      questionPackages: 0,
      quality: 0
    };

    const timer = setInterval(() => {
      current.students = Math.min(current.students + targets.students / steps, targets.students);
      current.courses = Math.min(current.courses + targets.courses / steps, targets.courses);
      current.questionPackages = Math.min(current.questionPackages + targets.questionPackages / steps, targets.questionPackages);
      current.quality = Math.min(current.quality + targets.quality / steps, targets.quality);

      setStats({
        students: Math.floor(current.students),
        courses: Math.floor(current.courses),
        questionPackages: Math.floor(current.questionPackages),
        quality: Math.floor(current.quality)
      });

      if (current.students >= targets.students) {
        clearInterval(timer);
      }
    }, increment);

    return () => clearInterval(timer);
  }, []);

  // Rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Rotate AI features in hero showcase
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveAIFeature((prev) => (prev + 1) % 6);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Rotate code examples
  useEffect(() => {
    const interval = setInterval(() => {
      setCodeExample((prev) => (prev + 1) % codeExamples.length);
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const codeExamples = [
    {
      language: 'Python',
      code: `def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

print(fibonacci(10))  # Output: 55`,
      color: 'from-yellow-400 to-amber-500'
    },
    {
      language: 'JavaScript',
      code: `const quickSort = (arr) => {
  if (arr.length <= 1) return arr;
  const pivot = arr[0];
  const left = arr.filter((x, i) => i > 0 && x < pivot);
  const right = arr.filter((x, i) => i > 0 && x >= pivot);
  return [...quickSort(left), pivot, ...quickSort(right)];
};`,
      color: 'from-yellow-300 to-yellow-500'
    },
    {
      language: 'Java',
      code: `public class BinarySearch {
    public static int search(int[] arr, int target) {
        int left = 0, right = arr.length - 1;
        while (left <= right) {
            int mid = left + (right - left) / 2;
            if (arr[mid] == target) return mid;
            if (arr[mid] < target) left = mid + 1;
            else right = mid - 1;
        }
        return -1;
    }
}`,
      color: 'from-red-400 to-orange-500'
    }
  ];

  const aiFeatures = [
    { 
      icon: '🤖', 
      title: 'AI Interview Simulator', 
      description: 'Practice technical interviews with our AI interviewer. Get language-specific problems, real-time feedback, and improve your interview skills.',
      gradient: 'from-purple-500 via-pink-500 to-red-500',
      link: '/interview-simulator',
      badge: 'NEW'
    },
    { 
      icon: '🧠', 
      title: 'AI Code Assistant', 
      description: 'Stuck on a problem? Our AI assistant provides intelligent hints, explains concepts, and helps debug your code in real-time.',
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      link: '/ai-assistant',
      badge: 'POPULAR'
    },
    { 
      icon: '🔍', 
      title: 'Code Smell Detector', 
      description: 'AI-powered code quality analyzer detects 15+ code smells, provides severity ratings, and suggests actionable improvements.',
      gradient: 'from-green-500 via-emerald-500 to-teal-500',
      link: '/ide',
      badge: 'AI'
    },
    { 
      icon: '🎮', 
      title: 'RPG Skill Tree', 
      description: 'Gamified learning system with XP, levels, and skill trees. Unlock Algorithm Mastery, Debugging Warrior, and Optimization Sage paths.',
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
      link: '/dashboard',
      badge: 'UNIQUE'
    },
    { 
      icon: '👻', 
      title: 'Ghost Coding', 
      description: 'Watch keystroke-by-keystroke replays of expert solutions. Learn coding patterns and problem-solving approaches from the best.',
      gradient: 'from-gray-500 via-slate-500 to-zinc-500',
      link: '/question-packages',
      badge: 'LEARN'
    },
    { 
      icon: '💻', 
      title: 'Advanced IDE', 
      description: 'Professional-grade IDE built into question packages. Write, test, and debug code with syntax highlighting, autocomplete, and real-time error detection.',
      gradient: 'from-orange-500 via-amber-500 to-yellow-500',
      link: '/question-packages',
      badge: 'PRO'
    }
  ];

  const features = [
    { icon: '🚀', title: 'Advanced IDE', description: 'Professional-grade development environment with debugging and code analysis', color: 'teal' },
    { icon: '📚', title: 'Interactive Learning', description: 'Learn programming through hands-on practice with real-time feedback', color: 'orange' },
    { icon: '👥', title: 'Live Collaboration', description: 'Code together with peers and mentors in real-time sessions', color: 'cyan' },
    { icon: '🎯', title: 'Practice Questions', description: 'Thousands of coding challenges from easy to expert level', color: 'purple' },
    { icon: '🏆', title: 'Certifications', description: 'Earn certificates upon completing courses and challenges', color: 'pink' },
    { icon: '🤖', title: 'AI Assistant', description: 'Get instant help from our AI-powered coding assistant', color: 'green' }
  ];


  const testimonials = [
    { 
      name: 'Unnati Kawade', 
      role: 'Software Engineer @ Volkswagen', 
      text: 'Coming from Mechanical Engineering, I had no confidence in coding. Your teaching style and guidance made DSA clear and easy. Now I feel confident building logic and don\'t fear DSA anymore. Thank you for this journey!', 
      avatar: '👩‍💻',
      image: '/images/unnati-kawade.jpg',
      linkedin: 'https://www.linkedin.com/in/unnati-kawade-46876b255/'
    },
    { 
      name: 'Rajni Kumari', 
      role: 'Software Engineer @ Lumiq', 
      text: 'Got placed in 2 great companies with decent packages! Rahul sir\'s guidance in DSA and Java was exactly what I needed. He taught me the importance of problem-solving and made me believe I can do it. Thank you for making this journey possible!', 
      avatar: '👩‍💼',
      image: '/images/rajni-kumari.png',
      linkedin: 'https://www.linkedin.com/in/rajnirajput20/'
    },
    { 
      name: 'Anshika Tiwari', 
      role: 'Software Engineer @ Coforge', 
      text: 'Rahul sir taught me the importance and real-world applications before diving into coding. He simplified complex DSA topics beautifully - exactly what someone new to coding needs. All my fears vanished, and I gained confidence. Thank you for making DSA so interesting!', 
      avatar: '👩‍💻',
      image: '/images/anshika-tiwari.png',
      linkedin: 'https://www.linkedin.com/in/anshika-tiwari-836926213/'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 animate-float"
            style={{
              width: `${Math.random() * 4 + 1}px`,
              height: `${Math.random() * 4 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 10}s`
            }}
          />
        ))}
      </div>

      {/* Compact Hero Section - Split Screen Layout */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-8 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center animate-fade-in">
          {/* Left Side - Content */}
          <div className="text-center lg:text-left">
            <div className="flex justify-center lg:justify-start mb-4">
              <YCodeThumbnail size="medium" />
          </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 animate-slide-up">
            Master Coding with{' '}
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                AI
            </span>
          </h1>
            <p className="text-sm sm:text-base text-orange-400 font-medium mb-2 animate-pulse-slow">
            🐛 Debugging the Road to Progress
          </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-4 sm:mb-5 max-w-xl mx-auto lg:mx-0 animate-slide-up delay-200">
            The next-generation coding platform powered by <span className="text-purple-400 font-semibold">Artificial Intelligence</span>. Learn smarter with AI interviews, intelligent code analysis, and gamified progress tracking.
          </p>
            <div className="mb-4 sm:mb-6 max-w-xl mx-auto lg:mx-0 animate-slide-up delay-300">
              <p className="text-base sm:text-lg md:text-xl text-teal-400 font-bold mb-2 text-center lg:text-left">
                ✨ Start free, upgrade when ready
              </p>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8 justify-center lg:justify-start animate-slide-up delay-400">
              <Link
                to="/interview-simulator"
                className="group relative bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden"
              >
                <span className="relative z-10">🤖 Try AI Interview</span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </Link>
              <Link
                to="/ide"
                className="group bg-transparent border-2 border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition duration-300 transform hover:scale-105 hover:shadow-2xl relative overflow-hidden"
              >
                <span className="relative z-10">💻 Start Coding →</span>
                <div className="absolute inset-0 bg-teal-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </Link>
              <Link
                to="/courses"
                className="group bg-transparent border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition duration-300 transform hover:scale-105 hover:shadow-2xl relative overflow-hidden"
              >
                <span className="relative z-10">📚 Browse Courses</span>
                <div className="absolute inset-0 bg-blue-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </Link>
            </div>

            {/* Feature Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
            <span className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 text-purple-300 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm">
              🤖 AI Interview Prep
            </span>
            <span className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 text-blue-300 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm">
              🧠 Smart Code Analysis
            </span>
            <span className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 text-green-300 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm">
              🎮 Gamified Learning
            </span>
          </div>
          
            {/* Enhanced Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 max-w-2xl mx-auto lg:mx-0">
              {[
                { label: 'Students', value: stats.students, suffix: '+', icon: '👨‍🎓', metric: 'Active Learners' },
                { label: 'Courses', value: stats.courses, suffix: '+', icon: '📚', metric: 'Available Now' },
                { label: 'Packages', value: stats.questionPackages, suffix: '+', icon: '📦', metric: 'Practice Sets' },
                { label: 'Quality', value: stats.quality, suffix: '%', icon: '⭐', metric: 'Satisfaction Rate' }
            ].map((stat, index) => (
              <div
                key={index}
                  className="bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-3 border border-white/20 hover:border-teal-400/50 transition-all duration-300 transform hover:scale-105 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                  <div className="text-lg sm:text-xl mb-1">{stat.icon}</div>
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                  {stat.value}{stat.suffix}
                  </div>
                  <div className="text-xs text-gray-400 font-semibold">{stat.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{stat.metric}</div>
              </div>
            ))}
          </div>
        </div>

          {/* Right Side - Visual Showcase */}
          <div className="relative hidden lg:block">
            <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl overflow-hidden border border-white/10 backdrop-blur-md p-6 transform hover:scale-105 transition-all duration-500">
            <div className={`absolute inset-0 bg-gradient-to-br ${aiFeatures[activeAIFeature].gradient} opacity-10`} />
              <div className="relative">
                <div className="text-center mb-4">
                  <div className="text-6xl mb-3 animate-bounce-slow">
                  {aiFeatures[activeAIFeature].icon}
                </div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-white">
                      {aiFeatures[activeAIFeature].title}
                    </h3>
                    <span className={`bg-gradient-to-r ${aiFeatures[activeAIFeature].gradient} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                      {aiFeatures[activeAIFeature].badge}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 mb-4">
                    {aiFeatures[activeAIFeature].description.substring(0, 100)}...
                  </p>
                  <button
                    onClick={() => handleAIFeatureClick(aiFeatures[activeAIFeature])}
                    className={`inline-block bg-gradient-to-r ${aiFeatures[activeAIFeature].gradient} text-white px-4 py-2 rounded-lg text-sm font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl cursor-pointer`}
                  >
                    Try It Now →
                  </button>
                </div>
                {/* Code Preview */}
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-400 ml-2">{codeExamples[codeExample].language}</span>
                  </div>
                  <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                    <code>{codeExamples[codeExample].code.substring(0, 150)}...</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <div className="inline-block bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-400/30 text-teal-300 px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
            🚀 Simple Process
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 px-4">
            How It <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Works</span>
        </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mx-auto px-4">
            Get started in minutes and begin your coding journey with AI-powered learning
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {[
            {
              step: '01',
              title: 'Sign Up & Choose Your Path',
              description: 'Create your free account and select your learning goal - interview prep, skill building, or project mastery.',
              icon: '👤',
              color: 'from-blue-500 to-cyan-500'
            },
            {
              step: '02',
              title: 'Practice with AI',
              description: 'Start coding with our AI Interview Simulator or practice questions. Get instant feedback and hints.',
              icon: '🤖',
              color: 'from-purple-500 to-pink-500'
            },
            {
              step: '03',
              title: 'Get Real-time Feedback',
              description: 'Receive intelligent code analysis, suggestions, and explanations to improve your coding skills.',
              icon: '💡',
              color: 'from-green-500 to-emerald-500'
            },
            {
              step: '04',
              title: 'Track Progress & Level Up',
              description: 'Monitor your growth with gamified progress tracking, achievements, and skill trees.',
              icon: '📈',
              color: 'from-orange-500 to-red-500'
            }
          ].map((item, index) => (
            <div
              key={index}
              className="relative bg-white/10 backdrop-blur-md rounded-xl p-6 sm:p-8 border border-white/20 hover:border-teal-400/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl"
            >
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-gray-800 to-gray-900 rounded-full border-2 border-teal-400 flex items-center justify-center">
                <span className="text-teal-400 font-bold text-lg">{item.step}</span>
              </div>
              <div className={`text-5xl sm:text-6xl mb-4 bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>
                {item.icon}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                {item.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                {item.description}
              </p>
              {index < 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-teal-400 text-2xl">
                  →
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials - Moved Higher for Social Proof */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-3 sm:mb-4 px-4">
          Loved by <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">Developers</span>
        </h2>
        <p className="text-sm sm:text-base text-gray-400 text-center mb-10 sm:mb-12 md:mb-16 max-w-2xl mx-auto px-4">
          Join thousands of developers who have transformed their careers with YCode
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-md rounded-lg p-5 sm:p-6 md:p-8 border border-white/20 hover:border-pink-400/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl"
            >
              {testimonial.image ? (
                <div className="mb-3 sm:mb-4 flex justify-center">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full object-cover border-4 border-purple-400/50 shadow-lg"
                  />
                </div>
              ) : (
                <div className="text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4 text-center">{testimonial.avatar}</div>
              )}
              <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 italic">"{testimonial.text}"</p>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm sm:text-base text-white font-semibold">{testimonial.name}</p>
                  {testimonial.linkedin && (
                    <a 
                      href={testimonial.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9 h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                </div>
                <p className="text-gray-400 text-xs sm:text-sm">{testimonial.role}</p>
              </div>
              <div className="flex mt-3 sm:mt-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-sm sm:text-base">★</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section - Hidden on mobile */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-3 sm:mb-4 px-4">
          Why Choose <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">YCode</span>?
        </h2>
        <p className="text-sm sm:text-base text-gray-400 text-center mb-10 sm:mb-12 md:mb-16 max-w-2xl mx-auto px-4">
          Everything you need to become a professional developer, all in one platform
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative bg-white/10 backdrop-blur-md rounded-lg p-5 sm:p-6 md:p-8 border border-white/20 hover:border-${feature.color}-400/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl cursor-pointer ${
                activeFeature === index ? `border-${feature.color}-400 shadow-lg` : ''
              }`}
              onMouseEnter={() => setActiveFeature(index)}
            >
              <div className={`text-4xl sm:text-5xl mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12`}>
                {feature.icon}
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-3 md:mb-4 group-hover:text-teal-400 transition-colors">
                {feature.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-300">
                {feature.description}
              </p>
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${feature.color}-400 to-${feature.color}-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-lg`} />
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-block bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 text-purple-300 px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
            ❓ Common Questions
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 px-4">
            Frequently Asked <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Questions</span>
            </h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto px-4">
            Everything you need to know about getting started with YCode
          </p>
        </div>

        <div className="space-y-4">
          {[
            {
              question: 'Is it free to start?',
              answer: 'Yes! You can create a free account and access our public IDE, try AI interviews, and explore many features without any credit card. Premium features like advanced courses and question packages are available with a one-time purchase.'
            },
            {
              question: 'Do I need prior coding experience?',
              answer: 'Not at all! YCode is designed for learners at all levels. We have beginner-friendly courses, step-by-step tutorials, and AI assistance that helps you learn from scratch. Our platform adapts to your skill level.'
            },
            {
              question: 'What programming languages are supported?',
              answer: 'We support 8+ popular languages including Python, JavaScript, Java, C++, TypeScript, Go, Rust, and SQL. Our IDE provides syntax highlighting, autocomplete, and debugging for all supported languages.'
            },
            {
              question: 'How does the AI Interview Simulator work?',
              answer: 'Our AI Interview Simulator provides realistic technical interview experiences. You choose your difficulty level and programming language, and our AI generates appropriate problems. You code in real-time, get instant feedback, and receive a comprehensive evaluation at the end.'
            },
            {
              question: 'Can I get a certificate upon completion?',
              answer: 'Yes! We offer certificates for completing courses and achieving milestones. These certificates can be shared on LinkedIn and added to your professional portfolio to showcase your skills.'
            },
            {
              question: 'Is my code and data secure?',
              answer: 'Absolutely. We use industry-standard encryption, secure data storage, and follow best practices for data protection. Your code and personal information are private and never shared with third parties.'
            },
            {
              question: 'What makes YCode different from other platforms?',
              answer: 'YCode combines AI-powered learning, gamified progress tracking, and real-time code analysis in one platform. Our unique features include AI Interview Simulator, RPG Skill Tree, Ghost Coding replays, and intelligent code quality detection - all designed to make learning coding more engaging and effective.'
            }
          ].map((faq, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 overflow-hidden transition-all duration-300 hover:border-purple-400/50"
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                className="w-full px-6 py-4 sm:px-8 sm:py-5 text-left flex items-center justify-between group"
              >
                <h3 className="text-sm sm:text-lg font-semibold text-white group-hover:text-purple-300 transition-colors pr-4">
                  {faq.question}
                </h3>
                <span className={`text-xl sm:text-2xl text-gray-400 transition-transform duration-300 flex-shrink-0 ${openFAQ === index ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {openFAQ === index && (
                <div className="px-6 sm:px-8 pb-4 sm:pb-5">
                  <p className="text-xs sm:text-base text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <div className="bg-gradient-to-r from-teal-900/30 to-cyan-900/30 backdrop-blur-md rounded-2xl p-6 sm:p-8 md:p-10 border border-teal-400/30 text-center">
          <div className="text-5xl sm:text-6xl mb-4">📧</div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            Stay Updated with <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">YCode</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-300 mb-6 max-w-2xl mx-auto">
            Get weekly coding tips, new feature updates, exclusive content, and learning resources delivered to your inbox.
          </p>
          {!newsletterSubmitted ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ email: newsletterEmail })
                  });
                  
                  if (response.ok) {
                    setNewsletterSubmitted(true);
                    setNewsletterEmail('');
                  } else {
                    const errorData = await response.json();
                    alert(errorData.error || 'Failed to subscribe. Please try again.');
                  }
                } catch (error) {
                  console.error('Error subscribing to newsletter:', error);
                  alert('Failed to subscribe. Please try again later.');
                }
              }}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition duration-300 transform hover:scale-105 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          ) : (
            <div className="bg-green-500/20 border border-green-400/50 rounded-lg px-6 py-4 max-w-md mx-auto">
              <p className="text-green-300 font-semibold">✓ Successfully subscribed</p>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-4">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/20 border-t border-white/20 py-8 sm:py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center justify-center mb-4">
                <YCodeThumbnail size="medium" />
              </div>
              <p className="text-gray-400 text-sm mb-2 text-center">
                Advanced coding platform for modern learners
              </p>
              <p className="text-orange-400 text-xs text-center">
                🐛 Debugging the Road to Progress
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Learn</h3>
              <ul className="space-y-2">
                <li><Link to="/courses" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">Courses</Link></li>
                <li><Link to="/question-packages" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">Practice</Link></li>
                {/* <li><Link to="/articles" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">Articles</Link></li> */}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Tools</h3>
              <ul className="space-y-2">
                <li><Link to="/ide" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">Online IDE</Link></li>
                <li><Link to="/ai-assistant" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">AI Assistant</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">About Us</Link></li>
                <li><button onClick={() => setShowContactModal(true)} className="text-gray-400 hover:text-teal-400 transition-colors text-sm sm:text-base">Contact</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 sm:pt-8 text-center">
            <p className="text-gray-400 text-xs sm:text-sm px-4">
              © 2024 YCode. All rights reserved. Made with ❤️ for developers.
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowContactModal(false);
            setContactForm({ name: '', email: '', message: '' });
            setContactSubmitted(false);
          }}
        >
          <div 
            className="bg-gray-900 rounded-2xl max-w-lg w-full shadow-2xl border border-white/20 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Contact Us
                </h2>
                <button
                  onClick={() => {
                    setShowContactModal(false);
                    setContactForm({ name: '', email: '', message: '' });
                    setContactSubmitted(false);
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>

              {contactSubmitted ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-400 mb-6">
                    We've received your message and will get back to you soon.
                  </p>
                  <button
                    onClick={() => {
                      setShowContactModal(false);
                      setContactForm({ name: '', email: '', message: '' });
                      setContactSubmitted(false);
                    }}
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const response = await fetch(`${API_URL}/api/contact/submit`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(contactForm)
                      });
                      
                      if (response.ok) {
                        setContactSubmitted(true);
                      } else {
                        const errorData = await response.json();
                        alert(`Failed to send message: ${errorData.error || 'Unknown error'}`);
                      }
                    } catch (error) {
                      console.error('Error submitting contact form:', error);
                      alert('Failed to send message. Please try again later.');
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="contactEmail"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactMessage" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="contactMessage"
                      required
                      rows="5"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowContactModal(false);
                        setContactForm({ name: '', email: '', message: '' });
                      }}
                      className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl max-w-md w-full p-8 shadow-2xl border border-white/20 transform animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-blue-500/10 rounded-2xl" />
            
            {/* Content */}
            <div className="relative">
              {/* Icon */}
              <div className="text-6xl mb-4 text-center animate-bounce-slow">
                {modalContent.icon}
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-white text-center mb-4">
                {modalContent.title}
              </h3>
              
              {/* Message */}
              <p className="text-gray-300 text-center mb-6 leading-relaxed">
                {modalContent.message}
              </p>
              
              {/* Buttons */}
              <div className="flex flex-col gap-3">
                {modalContent.buttons.map((button, index) => (
                  <button
                    key={index}
                    onClick={button.action}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                      button.primary
                        ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    }`}
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;

